import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuthContext } from '../contexts/AuthContext';
import { isOrgAdmin } from '../lib/orgRoles';
import api from '../lib/api';
import { getApiErrorMessage } from '../lib/apiErrors';
import {
  BILLING_CADENCES,
  BILLING_PLANS,
  COMMITMENT_TERM_MONTHS,
  buildPriceKey,
  cadenceDisplayName,
  planDisplayName,
  termDiscountPct,
  termDisplayName,
  type BillingCadence,
  type BillingPlan,
  type CommitmentTermMonths,
} from '../lib/billingCatalog';
import { ConfirmDialog } from '../components/ConfirmDialog';

interface BillingSummary {
  plan: BillingPlan | string;
  subscriptionStatus: string;
  trialEndsAt: string | null;
  planLabel: string;
  trialExpired: boolean;
  billingLocked: boolean;
  features: string[];
  hasStripeCustomer: boolean;
  stripeSubscriptionId: string | null;
  stripeScheduleId: string | null;
  billingPriceKey: string | null;
  billingCadence: BillingCadence | null;
  commitmentTermMonths: number | null;
  commitmentEndsAt: string | null;
  currentPeriodEndsAt: string | null;
}

interface BillingInvoiceRow {
  id: string;
  stripeInvoiceId: string;
  amountDueCents: number;
  amountPaidCents: number;
  currency: string;
  status: string;
  createdAt: string;
  paidAt: string | null;
  hostedInvoiceUrl: string | null;
}

const PLAN_BLURBS: Record<BillingPlan, string> = {
  starter: 'Solo practice, unlimited sessions, core coaching.',
  pro: 'Everything in Starter + multi-persona practice.',
  enterprise: 'Everything in Pro + multi-user sessions, scheduling, and priority support.',
};

export function BillingPage() {
  const { user } = useAuthContext();
  const [summary, setSummary] = useState<BillingSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<BillingPlan>('pro');
  const [selectedTerm, setSelectedTerm] = useState<CommitmentTermMonths>(0);
  const [selectedCadence, setSelectedCadence] = useState<BillingCadence>('monthly');
  const [promoCode, setPromoCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [invoices, setInvoices] = useState<BillingInvoiceRow[]>([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [planSwitchConfirmOpen, setPlanSwitchConfirmOpen] = useState(false);

  const canManageBilling = isOrgAdmin(user?.orgRole);

  const loadSummary = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<BillingSummary>('/billing/summary');
      setSummary(res.data);
      setError(null);
    } catch (err) {
      setError(getApiErrorMessage(err) || 'Failed to load billing');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadInvoices = useCallback(async () => {
    setInvoicesLoading(true);
    try {
      const res = await api.get<{ invoices?: BillingInvoiceRow[] }>('/billing/invoices');
      setInvoices(Array.isArray(res.data?.invoices) ? res.data.invoices : []);
    } catch {
      setInvoices([]);
    } finally {
      setInvoicesLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSummary();
    loadInvoices();
  }, [loadSummary, loadInvoices]);

  useEffect(() => {
    if (selectedTerm === 0 && selectedCadence === 'quarterly') {
      setSelectedCadence('monthly');
    }
  }, [selectedTerm, selectedCadence]);

  const priceKey = useMemo(
    () => buildPriceKey(selectedPlan, selectedTerm, selectedCadence),
    [selectedPlan, selectedTerm, selectedCadence],
  );

  const runCheckoutOrSwitch = async () => {
    if (!priceKey) {
      setError('Please pick a valid plan, term, and cadence.');
      return;
    }
    const isSwitch = Boolean(summary?.stripeSubscriptionId);
    setSubmitting(true);
    setError(null);
    try {
      if (isSwitch) {
        await api.post('/billing/update-subscription', { priceKey });
        await loadSummary();
        await loadInvoices();
        setSubmitting(false);
        return;
      }
      const res = await api.post<{ url: string; sessionId: string }>('/billing/checkout-session', {
        priceKey,
        promoCode: promoCode.trim() || undefined,
      });
      window.location.href = res.data.url;
    } catch (err) {
      setError(
        getApiErrorMessage(err) ||
          (isSwitch ? 'Could not change plan' : 'Could not start checkout'),
      );
      setSubmitting(false);
    }
  };

  const handleCheckoutOrSwitch = () => {
    if (!priceKey) {
      setError('Please pick a valid plan, term, and cadence.');
      return;
    }
    if (summary?.stripeSubscriptionId) {
      setPlanSwitchConfirmOpen(true);
      return;
    }
    void runCheckoutOrSwitch();
  };

  const handleManageBilling = async () => {
    setSubmitting(true);
    try {
      const res = await api.post<{ url: string }>('/billing/portal-session', {});
      window.location.href = res.data.url;
    } catch (err) {
      setError(getApiErrorMessage(err) || 'Could not open billing portal');
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="max-w-4xl mx-auto p-6 text-gray-600">Loading billing…</div>;
  }

  const commitmentActive = (summary?.commitmentTermMonths ?? 0) > 0;

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">
      <header>
        <h1 className="text-2xl font-bold text-gray-900">Billing</h1>
        <p className="text-sm text-gray-600 mt-1">
          Manage your Akoreps subscription, commitment term, and payment methods.
        </p>
      </header>

      {error && (
        <div
          role="alert"
          className="p-3 rounded border border-red-300 bg-red-50 text-sm text-red-800"
        >
          {error}
        </div>
      )}

      <section className="border border-gray-200 rounded-lg p-4 bg-white">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Current subscription</h2>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <div>
            <dt className="text-gray-500">Plan</dt>
            <dd className="text-gray-900 font-medium">{summary?.planLabel ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-gray-500">Status</dt>
            <dd className="text-gray-900 font-medium capitalize">
              {summary?.subscriptionStatus ?? '—'}
              {summary?.billingLocked && (
                <span className="ml-2 inline-block px-2 py-0.5 text-xs font-semibold bg-red-100 text-red-800 rounded">
                  locked
                </span>
              )}
            </dd>
          </div>
          {summary?.trialEndsAt && (
            <div>
              <dt className="text-gray-500">Trial ends</dt>
              <dd className="text-gray-900">
                {new Date(summary.trialEndsAt).toLocaleDateString()}
              </dd>
            </div>
          )}
          {commitmentActive && summary?.commitmentEndsAt && (
            <div>
              <dt className="text-gray-500">Commitment ends</dt>
              <dd className="text-gray-900">
                {new Date(summary.commitmentEndsAt).toLocaleDateString()} (
                {summary.commitmentTermMonths} months, {summary.billingCadence})
              </dd>
            </div>
          )}
          {summary?.currentPeriodEndsAt && (
            <div>
              <dt className="text-gray-500">Next invoice</dt>
              <dd className="text-gray-900">
                {new Date(summary.currentPeriodEndsAt).toLocaleDateString()}
              </dd>
            </div>
          )}
        </dl>

        {canManageBilling && summary?.hasStripeCustomer && (
          <div className="mt-4">
            <button
              type="button"
              onClick={handleManageBilling}
              disabled={submitting}
              className="px-4 py-2 text-sm font-medium text-gray-900 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-60"
            >
              Manage billing
            </button>
          </div>
        )}
      </section>

      <section className="border border-gray-200 rounded-lg p-4 bg-white">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Invoices</h2>
        {invoicesLoading ? (
          <p className="text-sm text-gray-600">Loading invoices…</p>
        ) : invoices.length === 0 ? (
          <p className="text-sm text-gray-600">No invoices yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="py-2 pr-4 font-medium">Date</th>
                  <th className="py-2 pr-4 font-medium">Amount</th>
                  <th className="py-2 pr-4 font-medium">Status</th>
                  <th className="py-2 font-medium">Receipt</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id} className="border-b border-gray-100">
                    <td className="py-2 pr-4 text-gray-900">
                      {new Date(inv.paidAt ?? inv.createdAt).toLocaleDateString()}
                    </td>
                    <td className="py-2 pr-4 text-gray-900">
                      {(inv.amountPaidCents / 100).toFixed(2)} {inv.currency.toUpperCase()}
                    </td>
                    <td className="py-2 pr-4 capitalize text-gray-700">{inv.status}</td>
                    <td className="py-2">
                      {inv.hostedInvoiceUrl ? (
                        <a
                          href={inv.hostedInvoiceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-green-700 hover:underline"
                        >
                          View
                        </a>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {canManageBilling && (
        <section
          aria-labelledby="pick-plan-heading"
          className="border border-gray-200 rounded-lg p-4 bg-white space-y-6"
        >
          <h2 id="pick-plan-heading" className="text-lg font-semibold text-gray-900">
            Pick a plan
          </h2>

          <div>
            <div className="text-sm font-medium text-gray-700 mb-2">Tier</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {BILLING_PLANS.map((plan) => (
                <label
                  key={plan}
                  className={`block cursor-pointer border rounded-lg p-3 text-sm ${
                    selectedPlan === plan
                      ? 'border-green-600 ring-2 ring-green-600 bg-green-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <input
                    type="radio"
                    name="plan"
                    value={plan}
                    checked={selectedPlan === plan}
                    onChange={() => setSelectedPlan(plan)}
                    className="sr-only"
                    aria-label={planDisplayName(plan)}
                  />
                  <div className="font-semibold text-gray-900">{planDisplayName(plan)}</div>
                  <div className="text-gray-600 mt-1">{PLAN_BLURBS[plan]}</div>
                </label>
              ))}
            </div>
          </div>

          <div>
            <div className="text-sm font-medium text-gray-700 mb-2">Commitment term</div>
            <div className="flex flex-wrap gap-2">
              {COMMITMENT_TERM_MONTHS.map((term) => (
                <label
                  key={term}
                  className={`cursor-pointer px-3 py-1.5 border rounded text-sm ${
                    selectedTerm === term
                      ? 'border-green-600 bg-green-50 text-green-900 font-medium'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <input
                    type="radio"
                    name="term"
                    value={term}
                    checked={selectedTerm === term}
                    onChange={() => setSelectedTerm(term)}
                    className="sr-only"
                    aria-label={termDisplayName(term)}
                  />
                  {termDisplayName(term)}
                  {termDiscountPct(term) > 0 && (
                    <span className="ml-1 text-xs text-green-700">−{termDiscountPct(term)}%</span>
                  )}
                </label>
              ))}
            </div>
          </div>

          <div>
            <div className="text-sm font-medium text-gray-700 mb-2">Billing cadence</div>
            <div className="flex gap-2">
              {BILLING_CADENCES.map((c) => {
                const disabled = selectedTerm === 0 && c === 'quarterly';
                return (
                  <label
                    key={c}
                    className={`px-3 py-1.5 border rounded text-sm ${
                      disabled
                        ? 'opacity-40 cursor-not-allowed border-gray-200'
                        : selectedCadence === c
                          ? 'border-green-600 bg-green-50 text-green-900 font-medium cursor-pointer'
                          : 'border-gray-300 hover:border-gray-400 cursor-pointer'
                    }`}
                  >
                    <input
                      type="radio"
                      name="cadence"
                      value={c}
                      checked={selectedCadence === c}
                      disabled={disabled}
                      onChange={() => setSelectedCadence(c)}
                      className="sr-only"
                      aria-label={cadenceDisplayName(c)}
                    />
                    {cadenceDisplayName(c)}
                  </label>
                );
              })}
            </div>
          </div>

          <div>
            <label htmlFor="promo" className="text-sm font-medium text-gray-700 block mb-1">
              Promo code (optional)
            </label>
            <input
              id="promo"
              type="text"
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value)}
              placeholder="LAUNCH20"
              className="w-full sm:w-64 px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-green-600"
            />
          </div>

          <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
            <div
              data-testid="selected-price-key"
              className="text-sm text-gray-500"
              aria-live="polite"
            >
              Selected: <span className="font-mono font-medium">{priceKey ?? 'n/a'}</span>
            </div>
            <div className="flex-1" />
            <button
              type="button"
              onClick={handleCheckoutOrSwitch}
              disabled={submitting || !priceKey}
              className="px-4 py-2 text-sm font-semibold text-white bg-green-700 rounded hover:bg-green-800 disabled:opacity-60"
            >
              {summary?.stripeSubscriptionId ? 'Switch plan' : 'Start checkout'}
            </button>
          </div>
        </section>
      )}

      <ConfirmDialog
        open={planSwitchConfirmOpen}
        title="Change subscription plan?"
        description="Change your subscription to the selected plan? Stripe will prorate the difference on your next invoice."
        confirmLabel="Change plan"
        onConfirm={() => {
          setPlanSwitchConfirmOpen(false);
          void runCheckoutOrSwitch();
        }}
        onCancel={() => setPlanSwitchConfirmOpen(false)}
        loading={submitting}
      />
    </div>
  );
}

export default BillingPage;
